import { ActivityTypeDefaultEnum, IsPregnantEnum, PregnancyCheckTermEnum, RealActivities } from 'openapi';
import { useCallback, useEffect } from 'react';
import { CalendarActivity } from 'utilities/Planning';
import { FieldArrayMethodProps, UseFieldArrayRemove, UseFormWatch } from 'react-hook-form';
import { MARE_CYCLE_DUAL_PRENANCY_SUBTRACT_DAYS, PregnancyCheckTermDaysEnum } from 'utilities/Breeding';
import type { AddFollowupMarecyclechecksDay } from '..';
import { useOrganization } from 'context/OrganizationContext';
import type { ExtendedMareCycleCheck } from '../index';

interface ReturnType {
  showFollowUpElement: boolean;
  suggestDateForFollowup: (term: PregnancyCheckTermEnum) => number;
}

interface Props {
  mareCycleCheckActivity: CalendarActivity | undefined;
  isPregnantFormValue: IsPregnantEnum | undefined;
  followupRemove: UseFieldArrayRemove;
  followupAppend: (value: AddFollowupMarecyclechecksDay | AddFollowupMarecyclechecksDay[], options?: FieldArrayMethodProps) => void;
  relatedInseminationActivity: RealActivities | undefined;
  watch: UseFormWatch<ExtendedMareCycleCheck>;
}

/**
 * Small hook that implement some logic for the followups of the mare cycle check when its a SCAN.
 */
function useMareCycleFollowUp({
  mareCycleCheckActivity,
  isPregnantFormValue,
  followupRemove,
  followupAppend,
  relatedInseminationActivity,
}: Props): ReturnType {
  const { selectedOrganizationDetails } = useOrganization();

  // flag that can be used to determine if we need to show the followup element where the user can add the followups
  const showFollowUpElement =
    (mareCycleCheckActivity !== undefined &&
      mareCycleCheckActivity.activityType?.default === ActivityTypeDefaultEnum.MARE_CYCLE_CHECK &&
      mareCycleCheckActivity.pregnancyCheckTerm === undefined &&
      isPregnantFormValue !== IsPregnantEnum.NO) ||
    mareCycleCheckActivity === undefined;

  const suggestDateForFollowup = useCallback(
    (term: PregnancyCheckTermEnum): number => {
      switch (term) {
        case PregnancyCheckTermEnum.FIRST:
          // This can be determined in the planning settings. By default it's 18 days.
          return selectedOrganizationDetails?.days_between_insemination_and_pregnancy_check ?? PregnancyCheckTermDaysEnum.FIRST;
        case PregnancyCheckTermEnum.SECOND:
          return PregnancyCheckTermDaysEnum.SECOND;
        case PregnancyCheckTermEnum.THIRD:
          return PregnancyCheckTermDaysEnum.THIRD;
      }
    },
    [selectedOrganizationDetails],
  );

  /**
   * If we are in a mare Cycle check for the first time (so just a CHECK) and the mare is pregnant
   * we should add 3 followups for the pregnancy check.
   *
   * This is only added when we are mark a activity as done.
   */
  useEffect(() => {
    if (
      mareCycleCheckActivity &&
      mareCycleCheckActivity.activityType?.default === ActivityTypeDefaultEnum.MARE_CYCLE_CHECK &&
      mareCycleCheckActivity.pregnancyCheckTerm === undefined
    ) {
      // first remove the olde ones
      followupRemove();

      if (isPregnantFormValue !== IsPregnantEnum.NO) {
        // if its a dual Pregnancy we should subtract 3 day from the first followup
        // otherwise we could the suggested date for the first followup
        let firstDays = suggestDateForFollowup(PregnancyCheckTermEnum.FIRST);
        if (isPregnantFormValue === IsPregnantEnum.DUAL_PREGNANCY) {
          firstDays = MARE_CYCLE_DUAL_PRENANCY_SUBTRACT_DAYS - MARE_CYCLE_DUAL_PRENANCY_SUBTRACT_DAYS;
        }

        followupAppend({
          days: firstDays,
          pregnancy_check_term: PregnancyCheckTermEnum.FIRST,
          order_item_uid: relatedInseminationActivity?.order_item_uid,
        });
        followupAppend({
          days: suggestDateForFollowup(PregnancyCheckTermEnum.SECOND),
          pregnancy_check_term: PregnancyCheckTermEnum.SECOND,
          order_item_uid: relatedInseminationActivity?.order_item_uid,
        });

        followupAppend({
          days: suggestDateForFollowup(PregnancyCheckTermEnum.THIRD),
          pregnancy_check_term: PregnancyCheckTermEnum.THIRD,
          order_item_uid: relatedInseminationActivity?.order_item_uid,
        });
      }
    }
  }, [
    followupAppend,
    followupRemove,
    isPregnantFormValue,
    mareCycleCheckActivity,
    relatedInseminationActivity?.order_item_uid,
    suggestDateForFollowup,
  ]);

  return {
    showFollowUpElement,
    suggestDateForFollowup,
  };
}

export default useMareCycleFollowUp;
